import EventEmitter from 'wolfy87-eventemitter';
import widgetSources from '../../../sources/widget';
import messager from '../../messager';
import MESSAGE_TYPES from '../../../constants/MESSAGE_TYPES';
import { IStandingTicketController } from './standingTicket.interface';
import { inject } from 'inversify';
import { ANNOUNCEMENTS_MODULE_IDS } from '../announcements/announcements.const';
import type { IAnnouncementsController } from '../announcements/announcements.types';
import { STANDING_TICKETS_MODULE_IDS } from './standingTickets.const';
import type { IStandingTicketsEvents } from './standingTickets.events';
import type { IAnnouncementEvents } from '../announcements/announcements.events';
import { sortBy } from 'lodash';

export default class StandingTicketsController
  extends EventEmitter
  implements IStandingTicketController
{
  data = [];
  totalSumBySectors = {};
  count: number = 0;

  constructor(
    @inject(ANNOUNCEMENTS_MODULE_IDS.ANNOUNCEMENTS_CONTROLLER)
    public announcementsController: IAnnouncementsController,
    @inject(ANNOUNCEMENTS_MODULE_IDS.ANNOUNCEMENTS_EVENTS)
    public announcementsEvents: IAnnouncementEvents,
    @inject(STANDING_TICKETS_MODULE_IDS.STANDING_TICKETS_EVENTS)
    public events: IStandingTicketsEvents,
  ) {
    super();

    announcementsController.addListener(
      announcementsEvents.SELECT_ANNOUNCEMENT,
      this.updateSelectedTickets.bind(this),
    );
  }

  loadStandingTickets = async () => {
    try {
      const { data: standingTickets } = await widgetSources.getStandTickets();
      const {
        announcementsController: { announcements },
      } = window.Widget;

      const { selectedTickets } = window.Widget.settings;

      this.data = standingTickets.map((item) => ({
        ...item,
        id: item.id || item.tickets[0].k,
        ticket: item.tickets[0].k,
      }));

      if (selectedTickets?.length) {
        this.data = this.data.map((standingTicket) => {
          const selectedAnnouncement = selectedTickets.find(
            (ticket) => ticket.id === standingTicket.id,
          );

          if (selectedAnnouncement) {
            return {
              ...standingTicket,
              selectedCount: selectedAnnouncement.count,
            };
          }

          return standingTicket;
        });
      }

      this.count = standingTickets.filter(
        (standingTicket) =>
          !announcements?.SECTOR?.some((announcement) => standingTicket.id === announcement.id),
      )?.length;

      this.addParserTicketsToAnnouncements(standingTickets);
      this.calcTotal();
    } catch (error) {}
  };

  addParserTicketsToAnnouncements = (standingTickets) => {
    const { selectedTickets } = window.Widget.settings;

    const parsersTickets = standingTickets
      .filter((t) => !t.id)
      .map((standingTicket) => {
        const id = standingTicket.tickets[0].k;
        const selectedAnnouncement = selectedTickets?.find((ticket) => ticket.id === id);
        return {
          ...standingTicket,
          id,
          ticket: standingTicket.tickets[0].k,
          selectedCount: selectedAnnouncement ? selectedAnnouncement.count : 0,
          view_scope_type: 'SECTOR',
        };
      });

    this.announcementsController.announcements.SECTOR = sortBy(
      [...this.announcementsController.announcements.SECTOR, ...parsersTickets],
      ['fragment.attributes.sector'],
    );
  };

  addTicket = (standingTicket, count) => {
    let { id, tickets } = standingTicket;

    if (!id) {
      id = tickets[0].k;
    }

    this.data = this.data.map((ticket) => {
      if (ticket.id === standingTicket.id) {
        return {
          ...ticket,
          selectedCount: count,
        };
      }

      return ticket;
    });

    const { selectedEvent, meta } = window.Widget.metaController;

    this.calcTotal();
    this.emit(this.events.SELECT_STANDING_TICKET, { id: standingTicket.id, newCount: count });

    messager.send({
      type: MESSAGE_TYPES.ON_ADD_STANDING_TICKETS,
      standingTicket: {
        ...standingTicket,
        id,
        selectedEvent: meta.isPeriodical ? selectedEvent : undefined,
      },
      count,
    });
  };

  removeTicket = (standingTicket) => {
    this.data = this.data.map((ticket) => {
      if (ticket.id === standingTicket.id) {
        return {
          ...ticket,
          selectedCount: 0,
        };
      }

      return ticket;
    });

    const { selectedEvent, meta } = window.Widget.metaController;

    this.calcTotal();
    this.emit(this.events.SELECT_STANDING_TICKET, { id: standingTicket.id, newCount: null });

    messager.send({
      type: MESSAGE_TYPES.ON_DELETE_STANDING_TICKETS,
      standingTicket: {
        ...standingTicket,
        selectedEvent: meta.isPeriodical ? selectedEvent : undefined,
      },
      count: 0,
    });
  };

  calcTotal = () => {
    this.totalSumBySectors = this.data.reduce((acc, ticket) => {
      const { sector } = ticket.fragment.attributes;
      acc[sector] = acc[sector] || 0;

      if (ticket.selectedCount) {
        acc[sector] += ticket.selectedCount * ticket.price;
      }

      return acc;
    }, {});

    this.emit(this.events.UPDATE_TOTAL);
  };

  updateSelectedTickets = (ticket) => {
    this.data = this.data.map((standingTicket) => {
      if (standingTicket.id === ticket.id) {
        return {
          ...standingTicket,
          selectedCount: ticket.newCount,
        };
      }

      return standingTicket;
    });
    this.calcTotal();
  };

  deleteTicket = (ticket) => {
    this.data = this.data.map((standingTicket) => {
      if (standingTicket.id === ticket.id) {
        return {
          ...standingTicket,
          selectedCount: 0,
        };
      }

      return standingTicket;
    });
    this.calcTotal();
    this.emit(this.events.REMOVE_TICKET, ticket);
  };
}
